export interface InstantFormReceived {
  count: number;
  contacts: number;
  first_at: string | null;
  last_at: string | null;
}

export interface InstantFormQuestion {
  key: string | null;
  label: string | null;
  type: string | null;
}

export interface InstantForm {
  id: string;
  name: string | null;
  status: string | null;
  locale: string | null;
  created_time: string | null;
  page_id: string;
  page_name: string | null;
  leads_count: number | null;
  organic_leads_count: number | null;
  expired_leads_count: number | null;
  higher_intent: boolean;
  hidden_from_organic: boolean;
  follow_up_action_url: string | null;
  privacy_policy_url: string | null;
  intro_title: string | null;
  thank_you: { title: string | null; button_type: string | null; button_text: string | null; website_url: string | null } | null;
  questions: InstantFormQuestion[];
  received: InstantFormReceived;
}

export type InstantFormsPageState = "ok" | "reconnect" | "no_permission" | "rate_limited" | "error";

export interface InstantFormsPage {
  page_id: string;
  page_name: string | null;
  state: InstantFormsPageState;
  forms: InstantForm[];
  truncated?: boolean;
  message?: string | null;
}

export interface InstantFormsRefresh {
  status: "refreshed" | "cooldown" | "busy" | "rate_limited";
  retry_after_seconds: number | null;
  available_at?: number | null;
}

export interface InstantFormsView {
  ad_account_id: string;
  ad_account_name: string | null;
  primary_page_id?: string | null;
  state: "ready" | "no_page";
  received_counts_available?: boolean;
  pages: InstantFormsPage[];
  refresh?: InstantFormsRefresh | null;
}

export const DEFAULT_FORM_STATUSES: readonly string[] = ["ACTIVE"];

const formStatus = (form: Pick<InstantForm, "status">) => String(form.status || "").toUpperCase() || "UNKNOWN";

export function matchesFormStatus(form: Pick<InstantForm, "status">, statuses: readonly string[]): boolean {
  return statuses.length === 0 || statuses.includes(formStatus(form));
}

export function visibleForms(
  forms: InstantForm[],
  { statuses, search }: { statuses: readonly string[]; search: string },
): InstantForm[] {
  const q = search.trim().toLowerCase();
  return forms
    .filter((form) => matchesFormStatus(form, statuses))
    .filter((form) => !q || String(form.name || "").toLowerCase().includes(q) || form.id.includes(q))
    .sort((a, b) => (Date.parse(b.created_time || "") || 0) - (Date.parse(a.created_time || "") || 0));
}

export function formStatusOptions(
  pages: InstantFormsPage[],
  selected: readonly string[],
): Array<{ id: string; label: string; meta: string }> {
  const counts = new Map<string, number>([
    ["ACTIVE", 0],
    ["ARCHIVED", 0],
  ]);
  for (const page of pages) for (const form of page.forms) counts.set(formStatus(form), (counts.get(formStatus(form)) ?? 0) + 1);
  for (const status of selected) if (!counts.has(status)) counts.set(status, 0);
  return [...counts.entries()].map(([id, n]) => ({ id, label: formStatusLabel(id), meta: String(n) }));
}

export type LeadCoverage =
  | { kind: "share"; percent: number; label: string }
  | { kind: "more"; label: string }
  | { kind: "none"; label: string };

export function leadCoverage(metaCount: number | null, received: number | null): LeadCoverage | null {
  if (metaCount === null || received === null || !Number.isFinite(metaCount) || !Number.isFinite(received)) return null;
  if (metaCount <= 0 && received <= 0) return { kind: "none", label: "No leads on this form yet" };
  if (received > metaCount) return { kind: "more", label: "WeNext holds more leads for this form than Meta's current count" };
  const raw = (received / metaCount) * 100;
  const percent = Math.min(100, Math.round(raw));
  const text = received > 0 && percent === 0 ? "<1%" : `${percent}%`;
  return { kind: "share", percent: received > 0 ? Math.max(1, percent) : 0, label: `${text} of Meta's leads are in WeNext` };
}

export function formsSummary(view: InstantFormsView | null | undefined): string | null {
  if (!view || view.state !== "ready") return null;
  const readable = view.pages.filter((p) => p.state === "ok");
  const total = readable.reduce((sum, p) => sum + p.forms.length, 0);
  const pages = view.pages.length;
  return `${total} ${total === 1 ? "form" : "forms"} on ${pages} ${pages === 1 ? "Page" : "Pages"}`;
}

export function formStatusLabel(status: string | null): string {
  const s = String(status || "").toUpperCase();
  if (!s) return "Unknown";
  return s.charAt(0) + s.slice(1).toLowerCase();
}

export function questionTypeLabel(type: string | null): string {
  const t = String(type || "").toUpperCase();
  if (!t) return "Question";
  if (t === "CUSTOM") return "Custom question";
  return t
    .split("_")
    .map((part, i) => (i === 0 ? part.charAt(0) + part.slice(1).toLowerCase() : part.toLowerCase()))
    .join(" ");
}

export function pageStateMessage(state: InstantFormsPageState): string | null {
  if (state === "reconnect") return "WeNext has no working access to this Page. Reconnect Meta to read its forms.";
  if (state === "no_permission") return "This login can't read this Page's forms. A Page admin needs to grant lead access.";
  if (state === "rate_limited") return "Meta asked WeNext to wait before reading this Page again. Try again in a few minutes.";
  if (state === "error") return "Meta didn't return this Page's forms. Try again in a moment.";
  return null;
}

const REFRESH_STATUSES: ReadonlySet<string> = new Set(["refreshed", "cooldown", "busy", "rate_limited"]);
const MAX_REFRESH_WAIT_SECONDS = 60 * 60;

export function stampFormsRefresh(view: InstantFormsView, now: number = Date.now()): InstantFormsView {
  const refresh = view?.refresh;
  if (!refresh || !REFRESH_STATUSES.has(refresh.status)) return view;
  const seconds = Number(refresh.retry_after_seconds);
  const wait = Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, MAX_REFRESH_WAIT_SECONDS) : 0;
  return { ...view, refresh: { ...refresh, available_at: wait ? now + wait * 1000 : null } };
}

export function formsRefreshWaitMs(view: InstantFormsView | null | undefined, now: number = Date.now()): number {
  const at = Number(view?.refresh?.available_at);
  if (!Number.isFinite(at)) return 0;
  return Math.max(0, at - now);
}

export function formsRefreshNotice(refresh: InstantFormsRefresh | null | undefined): { title: string; description: string } | null {
  if (!refresh || refresh.status === "refreshed" || !REFRESH_STATUSES.has(refresh.status)) return null;
  if (refresh.status === "cooldown") return { title: "Refreshed less than a minute ago", description: "Showing the forms already loaded." };
  if (refresh.status === "busy") {
    return { title: "Close to Meta's request limit", description: "Showing the saved forms so other Meta features keep working." };
  }
  return { title: "Meta is limiting requests", description: "Showing the saved forms." };
}
